import {
  faChevronRight,
  faSave,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import DatePickerComponent from 'components/common/DatePickerComponent';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import DialogModal from 'components/modals/DialogModal';
import Pagination from 'components/pagination';
import { formatDateTime, formatDateUs, useDebounce } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Row,
  Spinner
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import BillingService from 'services/Billing.service';
import CustomersService from 'services/Customers.service';
import InvoiceService from 'services/Invoice.service';
import TicketBillableItemService from 'services/TicketBillableItem.service';
import TicketsService from 'services/Tickets.service';
import GroupItemsLoader from './GroupItemsLoader';

const Billing = ({ onGenerateInvoice }) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [items, setItems] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 20,
    number: 0,
    totalElementsPerPage: 20,
    hasNextPage: false
  });

  const [groupingType, setGroupingType] = useState('none'); // 'none', 'customer', 'ticket'
  const [groups, setGroups] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 20,
    number: 0,
    totalElementsPerPage: 20,
    hasNextPage: false
  });
  const [groupFilters, setGroupFilters] = useState({
    page: 0,
    size: 20
  });
  const [expandedGroupKeys, setExpandedGroupKeys] = useState(new Set());

  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    billingStatus: 'PENDING',
    customerId: '',
    ticketId: '',
    startDate: '',
    endDate: ''
  });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [showCustomersSuggestions, setShowCustomersSuggestions] =
    useState(false);
  const [searchTermCustomer, setSearchTermCustomer] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [showTicketsSuggestions, setShowTicketsSuggestions] = useState(false);
  const [searchTermTicket, setSearchTermTicket] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getData = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        ...filters,
        page: filters.page,
        size: filters.size,
        billingStatus: filters.billingStatus || 'PENDING'
      }).toString();
      const response = await BillingService.listBillableItems(queryParams);
      if (response && response.content) {
        setItems({
          ...response,
          totalElementsPerPage: response.size || 20,
          hasNextPage: response.number < response.totalPages - 1
        });
      }
    } catch (error) {
      console.error('Erro ao buscar itens faturáveis:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getGroups = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: groupFilters.page,
        size: groupFilters.size,
        billingStatus: filters.billingStatus || 'PENDING',
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate })
      }).toString();

      let response;
      if (groupingType === 'customer') {
        response = await BillingService.getGroupedByCustomer(queryParams);
      } else if (groupingType === 'ticket') {
        response = await BillingService.getGroupedByTicket(queryParams);
      }

      if (response && response.content) {
        setGroups({
          ...response,
          totalElementsPerPage: response.size || 20,
          hasNextPage: response.number < response.totalPages - 1
        });
      }
    } catch (error) {
      console.error('Erro ao buscar grupos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (groupingType === 'none') {
      getData();
    } else {
      getGroups();
    }
  }, [filters, groupingType, groupFilters]);

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      startDate: startDate ? formatDateUs(startDate) : '',
      endDate: endDate ? formatDateUs(endDate) : '',
      page: 0
    }));
  }, [startDate, endDate]);

  const searchCustomers = async searchTerm => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomers([]);
      setShowCustomersSuggestions(false);
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 20,
        includeInactives: false,
        name: searchTerm
      }).toString();
      const response = await CustomersService.list(queryParams);
      if (response.content) {
        setCustomers(response.content);
        setShowCustomersSuggestions(true);
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const debouncedCustomerSearch = useDebounce(searchTerm => {
    searchCustomers(searchTerm);
  }, 400);

  useEffect(() => {
    if (searchTermCustomer.length >= 2) {
      debouncedCustomerSearch(searchTermCustomer);
    } else {
      setCustomers([]);
      setShowCustomersSuggestions(false);
    }
  }, [searchTermCustomer]);

  const searchTickets = async searchTerm => {
    if (!searchTerm || searchTerm.length < 2) {
      setTickets([]);
      setShowTicketsSuggestions(false);
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 20,
        code: searchTerm
      }).toString();
      const response = await TicketsService.list(queryParams);
      if (response.content) {
        setTickets(response.content);
        setShowTicketsSuggestions(true);
      }
    } catch (error) {
      console.error('Erro ao buscar tickets:', error);
    }
  };

  const debouncedTicketSearch = useDebounce(searchTerm => {
    searchTickets(searchTerm);
  }, 400);

  useEffect(() => {
    if (searchTermTicket.length >= 2) {
      debouncedTicketSearch(searchTermTicket);
    } else {
      setTickets([]);
      setShowTicketsSuggestions(false);
    }
  }, [searchTermTicket]);

  const handleCustomerSelect = customer => {
    setSelectedCustomer(customer);
    setSearchTermCustomer(customer.name || customer.fantasyName);
    setShowCustomersSuggestions(false);
    setFilters(prev => ({ ...prev, customerId: customer.id, page: 0 }));
  };

  const handleTicketSelect = ticket => {
    setSelectedTicket(ticket);
    setSearchTermTicket(ticket.code);
    setShowTicketsSuggestions(false);
    setFilters(prev => ({ ...prev, ticketId: ticket.id, page: 0 }));
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSearchTermCustomer('');
    setFilters(prev => ({ ...prev, customerId: '', page: 0 }));
  };

  const handleClearTicket = () => {
    setSelectedTicket(null);
    setSearchTermTicket('');
    setFilters(prev => ({ ...prev, ticketId: '', page: 0 }));
  };

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getBillingStatusBadge = status => {
    const variants = {
      PENDING: 'warning',
      INVOICED: 'info',
      CANCELED: 'danger',
      COMPLETED: 'success'
    };
    const labels = {
      PENDING: 'Pendente',
      INVOICED: 'Faturado',
      COMPLETED: 'Concluído',
      CANCELED: 'Cancelado'
    };
    return (
      <Badge bg={variants[status] || 'secondary'} className="fs-11">
        {labels[status] || status}
      </Badge>
    );
  };

  const handleGroupingChange = newGroupingType => {
    setGroupingType(newGroupingType);
    setGroupFilters({ page: 0, size: 20 });
    setExpandedGroupKeys(new Set());
    if (newGroupingType === 'none') {
      setFilters(prev => ({ ...prev, page: 0 }));
    }
  };

  const toggleGroup = groupKey => {
    setExpandedGroupKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const columns = [
    {
      accessorKey: 'customer.fantasyName',
      header: 'Cliente',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { customer } = rowData.row.original;
        return <div>{customer.fantasyName || `Cliente #${customer.id}`}</div>;
      }
    },
    {
      accessorKey: 'ticket.code',
      header: 'Ticket',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { ticket } = rowData.row.original;
        return <Link to={`/tickets/${ticket.id}`}>#{ticket.code}</Link>;
      }
    },
    {
      accessorKey: 'productService.name',
      header: 'Item',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { productService } = rowData.row.original;
        return <div>{productService.name || '-'}</div>;
      }
    },
    {
      accessorKey: 'quantity',
      header: 'Qtd',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { quantity } = rowData.row.original;
        return <div>{quantity}</div>;
      }
    },
    {
      accessorKey: 'unitPrice',
      header: 'Preço Unit.',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { unitPrice } = rowData.row.original;
        return <div>{formatCurrency(unitPrice)}</div>;
      }
    },
    {
      accessorKey: 'totalPrice',
      header: 'Preço Total',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { totalPrice } = rowData.row.original;
        return <div className="fw-bold">{formatCurrency(totalPrice)}</div>;
      }
    },
    {
      accessorKey: 'launchedAt',
      header: 'Data Lançamento',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { launchedAt } = rowData.row.original;
        return <div>{formatDateTime(launchedAt)}</div>;
      }
    },
    {
      accessorKey: 'billingStatus',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { billingStatus } = rowData.row.original;
        return getBillingStatusBadge(billingStatus);
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        const canDelete = item.billingStatus === 'PENDING';
        return (
          <Flex justifyContent="center" alignItems="center" className="gap-2">
            <Button
              variant="link"
              size="sm"
              className="p-1"
              onClick={e => {
                e.stopPropagation();
                handleDeleteItemClick(item);
              }}
              disabled={!canDelete || isDeleting}
              title={
                canDelete
                  ? 'Remover item'
                  : 'Apenas itens pendentes podem ser removidos'
              }
            >
              <FontAwesomeIcon icon={faTrash} className="text-danger" />
            </Button>
          </Flex>
        );
      }
    }
  ];

  const tableData = useMemo(() => {
    return items.content;
  }, [items.content]);

  // Função para verificar se um item pode ser selecionado
  // Usa selectedCustomerId diretamente para desabilitar checkboxes imediatamente
  const canSelectItem = useMemo(() => {
    return item => {
      // Se não há cliente selecionado, todos podem ser selecionados
      if (selectedCustomerId === null) return true;
      // Apenas itens do mesmo cliente podem ser selecionados
      return item.customer.id === selectedCustomerId;
    };
  }, [selectedCustomerId]);

  // Modificar dados da tabela para incluir informação de seleção
  const tableDataWithSelection = useMemo(() => {
    return tableData.map(item => ({
      ...item,
      canSelect: canSelectItem(item)
    }));
  }, [tableData, canSelectItem]);

  const table = useAdvanceTable({
    data: tableDataWithSelection,
    columns,
    selection: true,
    selectionColumnWidth: 50,
    getRowCanSelect: row => {
      return row.original.canSelect === true;
    }
  });

  // Atualizar selectedItems quando a seleção na tabela mudar
  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    const selectedIds = selectedRows.map(row => row.original.id);

    // Se não há itens selecionados, limpar tudo
    if (selectedIds.length === 0) {
      setSelectedItems([]);
      setSelectedCustomerId(null);
      return;
    }

    // Determinar o cliente selecionado PRIMEIRO (baseado no primeiro item selecionado)
    // Isso é importante para que canSelectItem funcione corretamente
    let newSelectedCustomerId = null;
    const firstSelectedItem = items.content.find(
      item => item.id === selectedIds[0]
    );
    if (firstSelectedItem) {
      newSelectedCustomerId = firstSelectedItem.customer.id;
    }

    // Filtrar apenas itens que podem ser selecionados (mesmo cliente)
    const validSelectedIds = selectedIds.filter(id => {
      const item = items.content.find(item => item.id === id);
      if (!item) return false;
      // Se não há cliente selecionado ainda, permite selecionar
      if (newSelectedCustomerId === null) return true;
      // Apenas itens do mesmo cliente podem ser selecionados
      return item.customer.id === newSelectedCustomerId;
    });

    // Atualizar estados - atualizar selectedCustomerId primeiro para que canSelectItem funcione
    setSelectedCustomerId(newSelectedCustomerId);
    setSelectedItems(validSelectedIds);

    // Se houver itens selecionados que não são válidos, limpar seleção da tabela
    if (selectedIds.length !== validSelectedIds.length) {
      const newSelection = {};
      validSelectedIds.forEach(id => {
        const rowIndex = tableDataWithSelection.findIndex(
          item => item.id === id
        );
        if (rowIndex !== -1) {
          newSelection[rowIndex] = true;
        }
      });
      table.setRowSelection(newSelection);
    }
  }, [
    table.getState().rowSelection,
    table,
    items.content,
    tableDataWithSelection
  ]);

  // Interceptar seleção de linhas para aplicar regra de negócio
  // Remove itens de outros clientes quando um cliente já está selecionado
  useEffect(() => {
    const rowSelection = table.getState().rowSelection || {};
    const hasSelection = Object.keys(rowSelection).length > 0;

    // Se não há seleção, não fazer nada (permite desmarcar tudo)
    if (!hasSelection) {
      return;
    }

    // Só interceptar se houver um cliente selecionado
    if (
      table &&
      tableDataWithSelection.length > 0 &&
      selectedCustomerId !== null
    ) {
      const currentSelection = rowSelection;
      const newSelection = {};

      Object.keys(currentSelection).forEach(rowIndex => {
        const row = tableDataWithSelection[parseInt(rowIndex)];
        if (row && canSelectItem(row)) {
          newSelection[rowIndex] = true;
        }
      });

      // Se houver diferença, atualizar seleção
      // Mas só se ainda houver itens válidos selecionados
      if (
        Object.keys(newSelection).length > 0 &&
        JSON.stringify(currentSelection) !== JSON.stringify(newSelection)
      ) {
        table.setRowSelection(newSelection);
      }
    }
  }, [table, tableDataWithSelection, selectedCustomerId, canSelectItem]);

  // Função para gerar fatura
  const handleGenerateInvoice = async () => {
    if (selectedItems.length === 0) return;

    if (!selectedCustomerId) {
      showErrorNotification(
        'Selecione itens de um cliente para gerar a fatura'
      );
      return;
    }

    setIsGeneratingInvoice(true);
    try {
      const response = await InvoiceService.create({
        billableItemIds: selectedItems,
        customerId: selectedCustomerId
      });

      if (response.id || response.status === 201 || response.status === 200) {
        showSuccessNotification('Fatura gerada com sucesso!');
        setSelectedItems([]);
        setSelectedCustomerId(null);
        // Limpar seleção da tabela
        table.setRowSelection({});
        // Recarregar dados
        if (groupingType === 'none') {
          getData();
        } else {
          getGroups();
        }
        // Mudar para aba de faturas se callback fornecido
        if (onGenerateInvoice) {
          onGenerateInvoice();
        }
      } else {
        showErrorNotification(response.userMessage || 'Erro ao gerar fatura');
      }
    } catch (error) {
      console.error('Erro ao gerar fatura:', error);
      showErrorNotification('Erro ao gerar fatura. Tente novamente.');
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleDeleteItemClick = item => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const handleConfirmDelete = async confirmed => {
    if (!confirmed) {
      handleCloseDeleteDialog();
      return;
    }

    if (!itemToDelete) {
      handleCloseDeleteDialog();
      return;
    }

    setIsDeleting(true);
    try {
      const response = await TicketBillableItemService.delete(
        itemToDelete.ticket.id,
        itemToDelete.id
      );

      if (
        response.status === 200 ||
        response.status === 204 ||
        !response.userMessage
      ) {
        showSuccessNotification('Item removido com sucesso');
        // Recarregar dados
        if (groupingType === 'none') {
          getData();
        } else {
          getGroups();
        }
        // Limpar seleção se o item deletado estava selecionado
        const updatedSelectedItems = selectedItems.filter(
          id => id !== itemToDelete.id
        );
        setSelectedItems(updatedSelectedItems);
        if (updatedSelectedItems.length === 0) {
          setSelectedCustomerId(null);
          table.setRowSelection({});
        }
      } else {
        showErrorNotification(response.userMessage || 'Erro ao remover item');
      }
    } catch (error) {
      console.error('Erro ao remover item:', error);
      showErrorNotification('Erro ao remover item');
    } finally {
      setIsDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  const isEmpty = !isLoading && items.content.length === 0;

  return (
    <>
      <div className="mb-3">
        <h6 className="mb-3">Filtros</h6>
        <div>
          <Row className="g-3">
            <Col md={2}>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filters.billingStatus}
                onChange={e =>
                  setFilters(prev => ({
                    ...prev,
                    billingStatus: e.target.value,
                    page: 0
                  }))
                }
              >
                <option value="PENDING" key="PENDING">
                  Pendente
                </option>
                <option value="INVOICED" key="INVOICED">
                  Faturado
                </option>
                <option value="COMPLETED" key="COMPLETED">
                  Concluído
                </option>
                <option value="CANCELED" key="CANCELED">
                  Cancelado
                </option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Cliente</Form.Label>
              <div className="position-relative">
                <SearchInputWithSuggestions
                  searchTerm={searchTermCustomer}
                  placeholder="Digite para buscar cliente..."
                  suggestions={customers}
                  showSuggestions={showCustomersSuggestions}
                  onSearchChange={e => {
                    setSearchTermCustomer(e.target.value);
                    setShowCustomersSuggestions(e.target.value.length > 0);
                  }}
                  onSuggestionSelect={handleCustomerSelect}
                  renderSuggestionItem={customer => (
                    <div
                      key={customer.id}
                      className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                      onClick={() => handleCustomerSelect(customer)}
                      role="button"
                    >
                      <div className="fw-bold">
                        {customer.type === 'JURIDICAL_PERSON'
                          ? customer.fantasyName
                          : customer.name}
                      </div>
                      <div className="text-muted small">
                        {customer.cpf || customer.cnpj}
                      </div>
                    </div>
                  )}
                />
                {selectedCustomer && (
                  <Button
                    size="sm"
                    variant="link"
                    className="position-absolute end-0 top-50 translate-middle-y me-2"
                    onClick={handleClearCustomer}
                  >
                    ×
                  </Button>
                )}
              </div>
            </Col>
            <Col md={2}>
              <Form.Label>Ticket</Form.Label>
              <div className="position-relative">
                <SearchInputWithSuggestions
                  searchTerm={searchTermTicket}
                  placeholder="Digite o código do ticket..."
                  suggestions={tickets}
                  showSuggestions={showTicketsSuggestions}
                  onSearchChange={e => {
                    setSearchTermTicket(e.target.value);
                    setShowTicketsSuggestions(e.target.value.length > 0);
                  }}
                  onSuggestionSelect={handleTicketSelect}
                  renderSuggestionItem={ticket => (
                    <div
                      key={ticket.id}
                      className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                      onClick={() => handleTicketSelect(ticket)}
                      role="button"
                    >
                      <div className="fw-bold">#{ticket.code}</div>
                      <div className="text-muted small">{ticket.subject}</div>
                    </div>
                  )}
                />
                {selectedTicket && (
                  <Button
                    size="sm"
                    variant="link"
                    className="position-absolute end-0 top-50 translate-middle-y me-2"
                    onClick={handleClearTicket}
                  >
                    ×
                  </Button>
                )}
              </div>
            </Col>
            <Col md={2}>
              <Form.Label>Agrupamento</Form.Label>
              <Form.Select
                value={groupingType}
                onChange={e => handleGroupingChange(e.target.value)}
              >
                <option value="none">Sem Agrupamento</option>
                <option value="customer">Agrupar por Cliente</option>
                <option value="ticket">Agrupar por Ticket</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Período</Form.Label>
              <DatePickerComponent
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                applyCustomStyles={false}
              />
            </Col>
          </Row>
        </div>
      </div>

      <div className="mb-3">
        <Flex justifyContent="between" alignItems="center" className="mb-3">
          <h6 className="mb-0">Itens Faturáveis</h6>
          <Flex alignItems="center" className="gap-2">
            <Button
              variant="primary"
              onClick={handleGenerateInvoice}
              disabled={selectedItems.length === 0 || isGeneratingInvoice}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              {isGeneratingInvoice ? 'Gerando...' : 'Gerar Fatura'}
            </Button>
          </Flex>
        </Flex>
        <div>
          {groupingType !== 'none' ? (
            <>
              {isLoading ? (
                <Flex justifyContent="center" className="py-4">
                  <Spinner animation="border" variant="primary" />
                </Flex>
              ) : groups.content.length === 0 ? (
                <EmptyList imgSize={500} vhSize={75} />
              ) : (
                <>
                  <div>
                    {groups.content.map(group => {
                      const groupKey = String(
                        groupingType === 'customer'
                          ? group.customerId
                          : group.ticketId
                      );
                      const isExpanded = expandedGroupKeys.has(groupKey);
                      const groupLabel =
                        groupingType === 'customer'
                          ? group.customerName || `Cliente #${group.customerId}`
                          : `Ticket #${group.ticketCode || group.ticketId}`;

                      const itemCountText =
                        group.itemCount === 1
                          ? '(1 item)'
                          : `(${group.itemCount} itens)`;

                      return (
                        <Card
                          key={groupKey}
                          className="mb-3 rounded border-0 shadow-sm"
                        >
                          <Card.Header
                            onClick={() => toggleGroup(groupKey)}
                            className="border-0 p-3 cursor-pointer user-select-none"
                          >
                            <Flex
                              className="w-100"
                              justifyContent="between"
                              alignItems="center"
                            >
                              <Flex alignItems="center">
                                <FontAwesomeIcon
                                  icon={faChevronRight}
                                  className="me-3 text-muted fs-10"
                                  style={{
                                    transition: 'transform 0.2s ease',
                                    transform: isExpanded
                                      ? 'rotate(90deg)'
                                      : 'rotate(0deg)'
                                  }}
                                />
                                <div>
                                  <strong className="text-900 fs-9">
                                    {groupLabel}
                                  </strong>
                                  <span className="text-muted ms-2 fs-9">
                                    {itemCountText}
                                  </span>
                                </div>
                              </Flex>
                              <div>
                                <span className="text-primary fw-semibold fs-9">
                                  {formatCurrency(group.totalValue)}
                                </span>
                              </div>
                            </Flex>
                          </Card.Header>
                          <Collapse in={isExpanded}>
                            <div>
                              <Card.Body className="pt-0 pb-3 ps-4">
                                {isExpanded && (
                                  <GroupItemsLoader
                                    groupType={groupingType}
                                    groupId={
                                      groupingType === 'customer'
                                        ? group.customerId
                                        : group.ticketId
                                    }
                                    filters={filters}
                                    onGenerateInvoice={onGenerateInvoice}
                                  />
                                )}
                              </Card.Body>
                            </div>
                          </Collapse>
                        </Card>
                      );
                    })}
                  </div>
                  <Pagination
                    data={groups}
                    filters={groupFilters}
                    setFilters={setGroupFilters}
                  />
                </>
              )}
            </>
          ) : (
            <>
              {isLoading ? (
                <Flex justifyContent="center" className="py-4">
                  <Spinner animation="border" variant="primary" />
                </Flex>
              ) : isEmpty ? (
                <EmptyList imgSize={500} vhSize={75} />
              ) : (
                <>
                  <AdvanceTableProvider {...table}>
                    <AdvanceTable
                      headerClassName="text-nowrap align-middle"
                      rowClassName="align-middle white-space-nowrap"
                      tableProps={{
                        striped: true,
                        className: 'fs-10 mb-0 overflow-hidden'
                      }}
                    />
                  </AdvanceTableProvider>
                  <Pagination
                    data={items}
                    filters={filters}
                    setFilters={setFilters}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      <DialogModal
        message="Tem certeza que deseja remover este item faturável?"
        showModal={showDeleteDialog}
        handleClose={handleCloseDeleteDialog}
        response={handleConfirmDelete}
      />
    </>
  );
};

export default Billing;
